/**
 * investorMath — pure recalculation helpers for the EditScenarioDialog.
 *
 * Mirrors the canonical math Three Little Birds uses when generating scenarios so
 * that a founder editing one independent input (ask, equity_pct,
 * valuation, income_share, cap_multiple) sees the dependent figures
 * update live without round-tripping through Claude.
 *
 * Independent vs derived inputs by roi_type:
 *
 *   equity:
 *     independent → ask_amount, equity_offered_pct, valuation_at_exit_usd, horizon
 *     derived     → yearly_returns (exit-weighted), total_return_multiple, irr_pct
 *
 *   income:
 *     independent → ask_amount, income_share_pct, income_cap_multiple, horizon
 *     derived     → yearly_returns (smooth-ramp), total_return_multiple, irr_pct
 *
 *   hybrid:
 *     independent → ask_amount, equity + income inputs above, horizon
 *     derived     → yearly_returns (income flow + equity exit bump),
 *                   total_return_multiple, irr_pct
 *
 * Everything is rounded to 2dp on the way out so the dialog text fields
 * don't render rats like 23456.78999999 from float arithmetic.
 */

/**
 * Compute IRR for a cash-flow series via bisection.
 *
 * `initial` is what the investor put in (positive number, paid at t=0).
 * `returns` is an ordered array of returns received at end of years
 * 1..H (positive numbers; year-1 first).
 *
 * Returns IRR as a PERCENTAGE (e.g. 0.22 → returns 22). Returns 0 when
 * the cash-flow series is degenerate (all zeros, total ≤ initial).
 */
export function computeIRR(initial, returns) {
  const init = Number(initial) || 0;
  const flows = (returns || []).map((r) => Number(r) || 0);
  if (init <= 0) return 0;
  const total = flows.reduce((a, b) => a + b, 0);
  if (total <= init) {
    // Loss case — return the linear approximation so the founder sees a
    // negative number rather than a stuck 0.
    const horizon = flows.length || 1;
    return Number((((total / init) ** (1 / horizon) - 1) * 100).toFixed(2));
  }
  // Bisection over the rate. Returns are positive and well-bounded by
  // 500% (Pydantic's irr_pct guard) so we can clamp the search window
  // safely.
  let lo = -0.99;
  let hi = 5.0;
  const npv = (rate) => -init + flows.reduce(
    (acc, r, i) => acc + r / (1 + rate) ** (i + 1),
    0,
  );
  for (let i = 0; i < 80; i += 1) {
    const mid = (lo + hi) / 2;
    const v = npv(mid);
    if (Math.abs(v) < 0.01 || hi - lo < 1e-6) {
      return Number((mid * 100).toFixed(2));
    }
    if (v > 0) lo = mid;
    else hi = mid;
  }
  return Number((((lo + hi) / 2) * 100).toFixed(2));
}

/** Round a USD amount to the nearest dollar. */
function r2(n) {
  return Math.round(Number(n) || 0);
}

/**
 * Distribute the total expected investor cash-out across a horizon for
 * an EQUITY scenario.
 *
 * Equity rounds typically return ~0 in years 1..H-1 and then the lump
 * sum at exit in year H. We bake a tiny 5%/10%/15% ramp into the
 * pre-exit years so the chart isn't completely flat for years 1-4 —
 * mirrors what Three Little Birds generates and what most founders expect on the
 * deck.
 */
export function distributeEquityReturns(totalCashOut, horizon) {
  const h = Math.max(1, Math.floor(horizon));
  const total = Number(totalCashOut) || 0;
  if (h === 1) return [{ year: 1, return_usd: r2(total) }];
  // Pre-exit years take ~12% of total (small dividends / interim
  // tenders). Exit year absorbs the remainder.
  const preExitTotal = total * 0.12;
  const out = [];
  let preExitSum = 0;
  for (let y = 1; y < h; y += 1) {
    // Weight ramps linearly toward exit year so years closer to liquidity
    // get a bigger pre-exit slice.
    const weight = y;
    const denom = (h - 1) * h / 2;
    const slice = preExitTotal * (weight / denom);
    const rounded = r2(slice);
    out.push({ year: y, return_usd: rounded });
    preExitSum += rounded;
  }
  out.push({ year: h, return_usd: r2(total - preExitSum) });
  return out;
}

/**
 * Income (revenue-share) returns smooth across the horizon because
 * payouts come from monthly revenue, not a binary liquidity event.
 *
 * Defaults to a gentle 1.0x→1.4x growth ramp so the early years look
 * smaller than the late years — matches what Three Little Birds emits and what
 * lenders/RBI investors expect.
 */
export function distributeIncomeReturns(totalCashOut, horizon) {
  const h = Math.max(1, Math.floor(horizon));
  const total = Number(totalCashOut) || 0;
  if (h === 1) return [{ year: 1, return_usd: r2(total) }];
  // Weighted geometric ramp — weight[y] = 1 + 0.4*(y-1)/(H-1).
  const weights = [];
  for (let y = 1; y <= h; y += 1) {
    weights.push(1 + 0.4 * ((y - 1) / (h - 1)));
  }
  const sumW = weights.reduce((a, b) => a + b, 0);
  const out = [];
  let accum = 0;
  for (let y = 1; y < h; y += 1) {
    const slice = r2(total * weights[y - 1] / sumW);
    out.push({ year: y, return_usd: slice });
    accum += slice;
  }
  // Last bucket absorbs the rounding drift so the sum == total exactly.
  out.push({ year: h, return_usd: r2(total - accum) });
  return out;
}

/**
 * Compute derived numbers for an EQUITY scenario from the independents.
 *
 * Returns { yearly_returns, total_return_multiple, irr_pct }.
 * Throws nothing — degenerate inputs (zero ask, zero equity) produce
 * zero-valued derived fields rather than NaNs so the form stays usable
 * mid-edit.
 */
export function computeEquityDerived({ ask_amount, equity_offered_pct, valuation_at_exit_usd, horizon_years }) {
  const ask = Number(ask_amount) || 0;
  const eq = Number(equity_offered_pct) || 0;
  const val = Number(valuation_at_exit_usd) || 0;
  const h = Math.max(1, Math.floor(Number(horizon_years) || 5));
  const investorExit = val * (eq / 100);
  const multiple = ask > 0 ? investorExit / ask : 0;
  const yearly_returns = distributeEquityReturns(investorExit, h);
  const irr_pct = computeIRR(ask, yearly_returns.map((r) => r.return_usd));
  return {
    yearly_returns,
    total_return_multiple: Number(multiple.toFixed(2)),
    irr_pct,
  };
}

/**
 * Compute derived numbers for an INCOME / revenue-share scenario.
 */
export function computeIncomeDerived({ ask_amount, income_cap_multiple, horizon_years }) {
  const ask = Number(ask_amount) || 0;
  const cap = Number(income_cap_multiple) || 0;
  const h = Math.max(1, Math.floor(Number(horizon_years) || 5));
  const totalCashOut = ask * cap;
  const yearly_returns = distributeIncomeReturns(totalCashOut, h);
  const multiple = ask > 0 ? totalCashOut / ask : 0;
  const irr_pct = computeIRR(ask, yearly_returns.map((r) => r.return_usd));
  return {
    yearly_returns,
    total_return_multiple: Number(multiple.toFixed(2)),
    irr_pct,
  };
}

/**
 * Hybrid = sum of the income flow + equity exit bump. Investor gets
 * monthly revenue-share payouts AND a smaller equity stake at exit.
 */
export function computeHybridDerived(inputs) {
  const equity = computeEquityDerived(inputs);
  const income = computeIncomeDerived(inputs);
  const ask = Number(inputs.ask_amount) || 0;
  const merged = equity.yearly_returns.map((row, i) => ({
    year: row.year,
    return_usd: r2((row.return_usd || 0) + (income.yearly_returns[i]?.return_usd || 0)),
  }));
  const total = merged.reduce((a, b) => a + (b.return_usd || 0), 0);
  const multiple = ask > 0 ? total / ask : 0;
  const irr_pct = computeIRR(ask, merged.map((r) => r.return_usd));
  return {
    yearly_returns: merged,
    total_return_multiple: Number(multiple.toFixed(2)),
    irr_pct,
  };
}

/**
 * Unified entrypoint — picks the right computer for the scenario's
 * roi_type. Returns null for unknown types so the dialog can fall back
 * to manual editing.
 */
export function recomputeDerived(scenario) {
  if (!scenario) return null;
  switch (scenario.roi_type) {
    case "equity": return computeEquityDerived(scenario);
    case "income": return computeIncomeDerived(scenario);
    case "hybrid": return computeHybridDerived(scenario);
    default: return null;
  }
}

/**
 * Extend (or trim) a yearly revenue series to `horizon` years using its own
 * trailing growth rate. Mirrors `project_revenue()` in backend/investor_math.py.
 */
export function projectRevenueSeries(basis, horizon) {
  const series = (basis || []).map((v) => Math.max(0, Number(v) || 0));
  const h = Math.max(1, Math.floor(Number(horizon) || 1));
  if (series.length === 0) return new Array(h).fill(0);
  let growth = 1;
  if (series.length >= 2 && series[series.length - 2] > 0) {
    growth = Math.max(1, series[series.length - 1] / series[series.length - 2]);
  }
  while (series.length < h) series.push(r2(series[series.length - 1] * growth));
  return series.slice(0, h);
}

/**
 * Implied exit revenue multiple — the sanity check on an exit valuation: how
 * many times the exit-year revenue an investor is being asked to believe.
 * Returns null when there's nothing to divide by.
 */
export function impliedExitRevenueMultiple({ valuation_at_exit_usd, plan, horizon_years }) {
  const val = Number(valuation_at_exit_usd) || 0;
  const basis = ((plan || {}).revenue_projection || []).map((r) => Number(r?.revenue) || 0);
  const horizon = Math.max(1, Math.floor(Number(horizon_years) || 5));
  if (val <= 0 || basis.length === 0) return null;
  const series = projectRevenueSeries(basis, horizon);
  const revenue = series[horizon - 1];
  if (!revenue) return null;
  return {
    multiple: Number((val / revenue).toFixed(1)),
    exit_year: horizon,
    exit_revenue: revenue,
    extrapolated: horizon > basis.length,
  };
}

/**
 * Rev-share terms solver (Jun 2026) — what a revenue-share deal actually
 * needs to repay its cap.
 *
 * Returns:
 *   share_needed_pct — the share of revenue required to hit the cap inside the
 *                      current horizon
 *   years_needed     — years required at the CURRENT share (null when it never
 *                      gets there inside `maxYears`)
 *   reaches_cap      — whether the current terms repay the cap in the horizon
 */
export function solveRevShareTerms({
  ask_amount_usd, income_cap_multiple, income_share_pct, horizon_years, plan, maxYears = 15,
}) {
  const ask = Number(ask_amount_usd) || 0;
  const cap = Number(income_cap_multiple) || 0;
  const share = Number(income_share_pct) || 0;
  const horizon = Math.max(1, Math.floor(Number(horizon_years) || 5));
  const basis = ((plan || {}).revenue_projection || []).map((r) => Number(r?.revenue) || 0);
  const capTotal = ask * cap;
  if (ask <= 0 || cap <= 0 || basis.length === 0) return null;

  const inHorizon = projectRevenueSeries(basis, horizon);
  const revenueInHorizon = inHorizon.reduce((a, b) => a + b, 0);
  const shareNeeded = revenueInHorizon > 0 ? (capTotal / revenueInHorizon) * 100 : null;

  let yearsNeeded = null;
  if (share > 0) {
    const long = projectRevenueSeries(basis, Math.max(horizon, maxYears));
    let paid = 0;
    for (let i = 0; i < long.length; i += 1) {
      paid += long[i] * (share / 100);
      if (paid >= capTotal) {
        yearsNeeded = i + 1;
        break;
      }
    }
  }

  return {
    cap_total_usd: r2(capTotal),
    revenue_in_horizon_usd: r2(revenueInHorizon),
    // Rounded UP to 1dp so applying the solved share actually clears the cap
    // (rounding down left it a few dollars short and the banner contradicted
    // itself).
    share_needed_pct: shareNeeded === null ? null : Math.ceil(shareNeeded * 10) / 10,
    years_needed: yearsNeeded,
    reaches_cap: yearsNeeded !== null && yearsNeeded <= horizon,
    horizon_years: horizon,
  };
}

/**
 * Detect whether two scenario states differ on any field that should
 * trigger a Three Little Birds narrative refresh. Returns the list of changed fields
 * so the UI can show "ask, valuation changed → refresh narrative?".
 */
const NARRATIVE_TRIGGERING_FIELDS = [
  "ask_amount_usd", "equity_offered_pct", "valuation_at_exit_usd",
  "income_share_pct", "income_cap_multiple",
  "total_return_multiple", "irr_pct",
];

export function changedNarrativeFields(a, b) {
  if (!a || !b) return [];
  const changed = [];
  for (const k of NARRATIVE_TRIGGERING_FIELDS) {
    const av = Number(a[k] ?? 0);
    const bv = Number(b[k] ?? 0);
    // Treat tiny rounding drift (< 1¢ / 0.01%) as no-change.
    if (Math.abs(av - bv) > 0.011) changed.push(k);
  }
  return changed;
}

/* ------------------------------------------------------------------ *
 * Jun 2026 · Sweat equity
 *
 * An investor can contribute WORK instead of cash. The deal still costs
 * the founder equity, but instead of the bank balance going up, the
 * monthly cost of that work goes away. Modelled as a monthly value over
 * a commitment length, so the relief EXPIRES the way a real engagement
 * does — the runway must not pretend a 6-month CMO is forever.
 * ------------------------------------------------------------------ */

/** Total value of contributed work on one scenario (monthly × months). */
export function sweatTotal(scenario) {
  const s = scenario || {};
  const monthly = Number(s.sweat_monthly_value_usd) || 0;
  const months = Number(s.sweat_months) || 0;
  return monthly > 0 && months > 0 ? monthly * months : 0;
}

/** Cash ask + contributed work — the basis every return figure measures against. */
export function investmentBasis(scenario) {
  return (Number((scenario || {}).ask_amount_usd) || 0) + sweatTotal(scenario);
}

/** Same day-of-month `n` months on, clamped to a shorter month. Mirrors the
 *  backend's `deadlines._add_months` so the reminder and the runway can never
 *  disagree about when a commitment ends. */
function addMonths(date, n) {
  const total = date.getMonth() + Number(n);
  const year = date.getFullYear() + Math.floor(total / 12);
  const month = ((total % 12) + 12) % 12;
  let day = date.getDate();
  // Feb 31st doesn't exist; walk back to the last real day of the month.
  const lastDay = new Date(year, month + 1, 0).getDate();
  if (day > lastDay) day = lastDay;
  return new Date(year, month, day);
}

/** The day the contributed work stops. */
export function sweatEndDate(scenario) {
  const s = scenario || {};
  const start = s.sweat_start_date || s.investment_start_date || "";
  const months = Number(s.sweat_months) || 0;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || months <= 0) return null;
  const d = new Date(`${start}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return addMonths(d, months);
}

/** Months of a commitment already served. Undated commitments are treated
 *  as starting today, so they read as fully remaining.
 *
 *  A plain month subtraction would call a 28-Mar → 28-Sep commitment "6 months
 *  served" on 7 Sep, three weeks early — which silently ended the cost relief
 *  before the reminder even fired. So the last month only counts once its
 *  day-of-month has actually come round. */
export function sweatMonthsElapsed(scenario, now = new Date()) {
  const s = scenario || {};
  const start = s.sweat_start_date || s.investment_start_date || "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) return 0;
  const d = new Date(`${start}T00:00:00`);
  if (Number.isNaN(d.getTime())) return 0;
  let months =
    (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  if (months > 0 && addMonths(d, months) > now) months -= 1;
  return Math.max(0, months);
}

/** True while a funded sweat commitment is still delivering work. */
export function sweatIsActive(scenario, now = new Date()) {
  const s = scenario || {};
  const monthly = Number(s.sweat_monthly_value_usd) || 0;
  const months = Number(s.sweat_months) || 0;
  if (monthly <= 0 || months <= 0) return false;
  return sweatMonthsElapsed(s, now) < months;
}

/**
 * Monthly cost relief from every FUNDED sweat commitment still running,
 * plus a per-commitment breakdown so the UI can show its own lane
 * ("$4,000/mo of work · 4 of 6 months left") instead of a silent discount.
 */
export function sweatBurnRelief(scenarios, now = new Date()) {
  const lanes = (scenarios || [])
    .filter((s) => s && s.status === "funded" && sweatIsActive(s, now))
    .map((s) => {
      const months = Number(s.sweat_months) || 0;
      const elapsed = sweatMonthsElapsed(s, now);
      return {
        id: s.id,
        role: s.sweat_role || "Contributed work",
        investor_name: s.investor_name || "",
        monthly_usd: Number(s.sweat_monthly_value_usd) || 0,
        months,
        months_remaining: Math.max(0, months - elapsed),
        budget_category: s.sweat_budget_category || "",
        total_usd: sweatTotal(s),
      };
    });
  return {
    monthly_usd: lanes.reduce((a, l) => a + l.monthly_usd, 0),
    lanes,
  };
}

/* ------------------------------------------------------------------ *
 * Jun 2026 · Equity split warning
 *
 * A founder can lose control one reasonable-looking deal at a time.
 * This totals the equity promised across every strategy that isn't
 * closed — the worst case if they ALL land — and compares it against a
 * threshold the founder sets per plan. It warns; it never blocks.
 * ------------------------------------------------------------------ */

export const DEFAULT_EQUITY_WARN_PCT = 25;

/** Statuses that still represent equity you could end up giving away. */
const LIVE_EQUITY_STATUSES = ["draft", "pitching", "committed", "funded"];

export function liveEquityPromised(scenarios) {
  const live = (scenarios || []).filter(
    (s) => s && LIVE_EQUITY_STATUSES.includes(s.status || "draft"),
  );
  const promised = live.reduce((a, s) => a + (Number(s.equity_offered_pct) || 0), 0);
  const committed = live
    .filter((s) => s.status === "committed" || s.status === "funded")
    .reduce((a, s) => a + (Number(s.equity_offered_pct) || 0), 0);
  return {
    promised_pct: Math.round(promised * 100) / 100,
    committed_pct: Math.round(committed * 100) / 100,
    live_count: live.length,
    sweat_pct:
      Math.round(
        live
          .filter((s) => sweatTotal(s) > 0)
          .reduce((a, s) => a + (Number(s.equity_offered_pct) || 0), 0) * 100,
      ) / 100,
  };
}

/** Would this strategy's equity push the total past the threshold? */
export function equityWarning(scenarios, thresholdPct = DEFAULT_EQUITY_WARN_PCT) {
  const t = Number(thresholdPct) || DEFAULT_EQUITY_WARN_PCT;
  const totals = liveEquityPromised(scenarios);
  return {
    ...totals,
    threshold_pct: t,
    over: totals.promised_pct > t,
    retained_pct: Math.round((100 - totals.promised_pct) * 100) / 100,
  };
}
